// A short memory of what touched the canvas, so a palm that still zooms the page on someone's iPad can be looked at
// afterwards. When the zoom changes around the moment the pencil lands, the last few seconds are kept as a report.
// Nothing leaves the device unless the user taps "Send a touch report".

export interface LogEntry {
  /** ms since the page loaded */
  t: number;
  /** what happened: down, move, up, cancel, touch, gesture, zoom, palm, reject, ... */
  e: string;
  /** pointer type, or a reason */
  k?: string;
  id?: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  /** buttons, touch count, or gesture scale */
  n?: number;
  z?: number;
}

export interface TouchReport {
  at: number;
  why: string;
  entries: LogEntry[];
}

const CAP = 3000;
const KEY = "sepia.touchReports";
const r1 = (v: number) => Math.round(v * 10) / 10;

export class InputLog {
  private buf: LogEntry[] = [];
  private lastZoomAt = -Infinity;
  private lastPenAt = -Infinity;
  private pending: { why: string; from: number } | null = null;
  private lastCapture = -Infinity;
  reports: TouchReport[] = [];

  constructor() {
    try {
      this.reports = JSON.parse(localStorage.getItem(KEY) || "[]");
    } catch {
      this.reports = [];
    }
  }

  add(e: LogEntry): void {
    for (const k of ["x", "y", "w", "h", "n"] as const) if (e[k] !== undefined) e[k] = r1(e[k]!);
    this.buf.push(e);
    if (this.buf.length > CAP) this.buf.splice(0, this.buf.length - CAP);
  }

  pointer(type: string, ev: PointerEvent, x: number, y: number): void {
    this.add({ t: ev.timeStamp || performance.now(), e: type, k: ev.pointerType, id: ev.pointerId, x, y, w: ev.width, h: ev.height, n: ev.buttons });
  }

  /** The zoom changed: if the pencil just landed, keep what led up to it. */
  zoomed(z: number, source: string): void {
    const t = performance.now();
    this.add({ t, e: "zoom", k: source, z: Math.round(z * 10000) / 10000 });
    this.lastZoomAt = t;
    if (t - this.lastPenAt < 1500) this.schedule("zoom just after the pencil landed (" + source + ")", this.lastPenAt - 4000);
  }

  /** The pencil landed: if the zoom changed just before, keep it. */
  penDown(): void {
    const t = performance.now();
    this.lastPenAt = t;
    if (t - this.lastZoomAt < 3000) this.schedule("zoom just before the pencil landed", t - 5000);
  }

  private schedule(why: string, from: number): void {
    if (this.pending || performance.now() - this.lastCapture < 4000) return;
    this.pending = { why, from };
    window.setTimeout(() => {
      const p = this.pending!;
      this.pending = null;
      this.lastCapture = performance.now();
      this.reports = [...this.reports, { at: Date.now(), why: p.why, entries: this.buf.filter((x) => x.t >= p.from) }].slice(-4);
      try {
        localStorage.setItem(KEY, JSON.stringify(this.reports));
      } catch {
        /* full: keep them in memory */
      }
    }, 1500);
  }

  /** Everything worth sending: the saved reports plus the last few seconds. */
  bundle(): { reports: TouchReport[]; recent: LogEntry[] } {
    const now = performance.now();
    return { reports: this.reports, recent: this.buf.filter((x) => x.t >= now - 8000) };
  }

  clear(): void {
    this.reports = [];
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }
}
